import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/lib/ui/button';

import { useBookingExtrasDeps } from './context';
import { useJoinMyWaitlist } from './queries';

/**
 * §6's client row: "This time is taken — tell me if it frees".
 *
 * **This one line goes through i18next**, unlike every other string in
 * this feature. §9 says so and BP-19's reason holds: the client app is
 * translated into hi, mr and gu, and an English sentence in the middle
 * of a Hindi booking flow is the one place a salon's client would
 * notice. The staff screens stay English (`strings.ts`).
 *
 * The join takes no `Idempotency-Key`: it creates no money and no
 * message, and a duplicate entry is a row the sweep passes over.
 *
 * Her id is never sent. The route takes it from her session — which is
 * what stops one client putting another on a list — so the draft here
 * carries only what she is waiting *for*.
 */
export function WaitlistJoin({
  serviceIds,
  windowFrom,
  windowTo,
}: {
  serviceIds: string[];
  windowFrom: string;
  windowTo: string;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const { writable } = useBookingExtrasDeps();
  const join = useJoinMyWaitlist();

  if (join.isSuccess) {
    return (
      <p role="status" data-testid="waitlist-joined">
        {t('waitlist.joined')}
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="waitlist-join">
      <p>{t('waitlist.offer')}</p>
      <Button
        type="button"
        disabled={!writable || join.isPending}
        onClick={() => {
          join.mutate({
            service_ids: serviceIds,
            window_from: windowFrom,
            window_to: windowTo,
          });
        }}
      >
        {t('waitlist.join')}
      </Button>
      {join.isError ? (
        <p role="alert" data-testid="waitlist-join-failed">
          {t('waitlist.failed')}
        </p>
      ) : null}
    </div>
  );
}
